import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import get from 'lodash/get';
import { connect, useDispatch } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { makeStyles } from 'src/makeStyles';
import { Grid } from '@mui/material';
import { PAGE_SUB_TYPE, PAGE_TYPE } from 'constants/footPrintTracking';
import useFootPrintTracker from 'components/Globals/FootPrintTracking';
import { useTranslation } from '../../src/i18n';
import RentalsSearchForm, { rentalsSearchFormFields } from './components/RentalsSearchForm';
import { selectRentals } from './selectors';
import FbPagination from '../../components/FbPagination';
import { getRentals } from './actions';
import FbCircularProgress from '../../components/FbCircularProgress';
import RentalCard from '../../components/RentalCard';
import ListingPageLayout from '../../components/ListingPageLayout';
import { TP, PRODUCTION_VALIDITY_IDENTIFIERS } from '../../constants/consts';
import { useList } from '../../utils/crudUtils';
import { useFormFilters } from '../../utils/formUtils';
import { useIsMount } from '../../hooks/useIsMount';

const useStyles = makeStyles()(theme => ({
  content: {
    paddingTop: '3rem',
    width: '100%',
    margin: '-8px',
    [theme.breakpoints.down('md')]: {
      paddingTop: 0,
      margin: 0,
    },
  },
}));

const trackingData = {
  pageType: PAGE_TYPE.LISTING_PAGE,
  pageSubType: PAGE_SUB_TYPE.RENTAL,
};

const Rentals = ({ rentalsData, customBreadcrumbs, queryValues, disableQuery, pageQueryList }) => {
  const { t } = useTranslation();
  const { classes, cx } = useStyles();
  const dispatch = useDispatch();
  const [formQuery, setQuery] = React.useState({});
  const router = useRouter();
  const trackFingerPrint = useFootPrintTracker();
  const isMount = useIsMount();

  const routerQuery = router.query?.letter || router.query;
  const validQuery = routerQuery && Object.keys(routerQuery).length !== 0;

  const [rentals, pagination, listFilters, loading] = useList(rentalsData);
  const [formValues] = useFormFilters(rentalsSearchFormFields, queryValues, listFilters);

  useEffect(() => {
    if (!isMount) {
      trackFingerPrint(trackingData);
    }
  }, [router?.query]);

  const fetchFunction = (query, source = 'pagination') => {
    let searchQuery = formQuery;

    if (source === 'pagination') {
      searchQuery = { ...searchQuery, ...query };
      trackFingerPrint(trackingData);
    } else {
      setQuery(query);
      searchQuery = query;
    }
    dispatch(
      getRentals({
        ...searchQuery,
        sort: get(query, 'sort') || '-year',
        published: true,
        has_active_rental: true,
        approved_by: [
          PRODUCTION_VALIDITY_IDENTIFIERS.COMPANY,
          PRODUCTION_VALIDITY_IDENTIFIERS.ADMIN,
          PRODUCTION_VALIDITY_IDENTIFIERS.FESTIVAL,
        ],
      }),
    );
  };

  const content = (
    <Grid container spacing={2} className={classes.content} justifyContent="center">
      {loading ? (
        <FbCircularProgress />
      ) : (
        <>
          {rentals?.length ? (
            <>
              {rentals.map((rental, index) => (
                <RentalCard rental={rental} key={index} />
              ))}
              <FbPagination filters={pagination} fetchFunction={fetchFunction} />
            </>
          ) : (
            t(`${TP}.NO_RESULTS`)
          )}
        </>
      )}
    </Grid>
  );

  return (
    <ListingPageLayout
      title={t(`${TP}.m_RENTALS`)}
      seo={{
        title: t(`${TP}.SEO_TITLE_RENTALS_INDEX`),
        description: t(`${TP}.SEO_DESC_RENTALS_INDEX`),
        ogTitle: t(`${TP}.SEO_OG_TITLE_RENTALS_INDEX`),
        other: {
          noindex: validQuery,
          follow: validQuery,
        },
      }}
      subTitle={t(`${TP}.m_RENTALS_FIND_RENTALS_FOR_PRODUCTIONS`)}
      content={content}
      searchFiltersComponent={
        <RentalsSearchForm getRentals={fetchFunction} initialValues={formValues} initialSortName={queryValues?.sort} />
      }
      customBreadcrumbs={customBreadcrumbs}
      disableQuery={disableQuery}
      pageQueryList={pageQueryList}
    />
  );
};

Rentals.propTypes = {
  rentalsData: PropTypes.array,
  customBreadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      path: PropTypes.string,
      title: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  ),
};

const mapStateToProps = createStructuredSelector({
  rentalsData: selectRentals(),
});

const withConnect = connect(mapStateToProps, null);

export default withConnect(Rentals);
